import { type PropsWithChildren, type ReactElement } from 'react';

import type { TruncationMode } from '@dynatrace/strato-components/typography';

import { StoreProvider } from './StoreProvider.js';
import { ChartSeriesActionContext } from '../../core/components/series-action/ChartSeriesActionContext.js';
import type {
  ColorPalette,
  CustomColorPalette,
} from '../../core/types/color-palette.js';
import type { ColoredRange } from '../../core/types/colored-range.js';
import type { LegendProps } from '../../core/types/legend.js';
import type { ChartToolbarConfig } from '../../core/types/toolbar.js';
import { ChartLegendContext } from '../context/ChartLegendContext.js';
import { ColorPaletteContext } from '../context/ColorPaletteContext.js';
import { FormatterContext } from '../context/FormatterContext.js';
import { LabelsDisplayContext } from '../context/LabelsDisplayContext.js';
import { MinMaxConfigContext } from '../context/MinMaxConfigContext.js';
import { NameAccessorContext } from '../context/NameAccessorContext.js';
import { ToolbarContext } from '../context/ToolbarContext.js';
import { TreeMapRawDataContext } from '../context/TreeMapRawDataContext.js';
import type { TreeMapFormatter } from '../types/internal.js';
import type {
  LabelsDisplay,
  NameAccessor,
  TreeMapData,
  TreeMapLeafNode,
} from '../types/treemap.js';

interface TreeMapChartProvidersProps extends PropsWithChildren {
  data: TreeMapData;
  seriesActions?: (node: TreeMapLeafNode) => ReactElement;
  formatter: TreeMapFormatter;
  truncationMode: TruncationMode;
  legend: LegendProps;
  toolbar?: ChartToolbarConfig;
  labelsDisplay: LabelsDisplay;
  nameAccessor?: NameAccessor;
  colorPalette: ColorPalette | CustomColorPalette | ColoredRange[];
  min?: 'data-min' | number;
  max?: 'data-max' | number;
}

export const TreeMapChartProviders = (props: TreeMapChartProvidersProps) => {
  const {
    data,
    seriesActions,
    formatter,
    truncationMode,
    legend,
    toolbar,
    labelsDisplay,
    nameAccessor,
    colorPalette,
    min: configMin,
    max: configMax,
    children,
  } = props;

  const min = configMin === 'data-min' ? undefined : configMin;
  const max = configMax === 'data-max' ? undefined : configMax;

  return (
    <TreeMapRawDataContext.Provider value={data}>
      <ChartSeriesActionContext.Provider
        value={{
          // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
          // @ts-ignore Strict mode transition - needs to be investigated
          chartSeriesActions: seriesActions,
        }}
      >
        <FormatterContext.Provider
          value={{
            formatter,
            truncationMode,
          }}
        >
          <ColorPaletteContext.Provider value={colorPalette}>
            <ChartLegendContext.Provider value={legend}>
              <NameAccessorContext.Provider value={nameAccessor}>
                <ToolbarContext.Provider value={toolbar}>
                  <LabelsDisplayContext.Provider value={labelsDisplay}>
                    <MinMaxConfigContext.Provider value={{ min, max }}>
                      <StoreProvider>{children}</StoreProvider>
                    </MinMaxConfigContext.Provider>
                  </LabelsDisplayContext.Provider>
                </ToolbarContext.Provider>
              </NameAccessorContext.Provider>
            </ChartLegendContext.Provider>
          </ColorPaletteContext.Provider>
        </FormatterContext.Provider>
      </ChartSeriesActionContext.Provider>
    </TreeMapRawDataContext.Provider>
  );
};

TreeMapChartProviders['displayName'] = 'TreeMapChartProviders';
